import type {
  CategoryScore,
  CompanyType,
  QuickscanAnswers,
  QuickscanResult,
} from '@wtta/scoring'

import type { WidgetConfig } from '../config'

const ANSWER_FIELD_PREFIX = 'qs_answer_'
const CATEGORY_FIELD_PREFIX = 'qs_cat_'

export interface BuildPardotUrlInput {
  config: WidgetConfig
  companyType: CompanyType
  answers: QuickscanAnswers
  result: QuickscanResult
  token: string
  successRedirectUrl?: string
}

export function buildPardotFormUrl(input: BuildPardotUrlInput): string {
  const { config, companyType, answers, result, token, successRedirectUrl } = input

  const qsParams: Array<[string, string]> = [
    ['qs_score', String(result.readinessPercentage)],
    ['qs_risk_level', result.riskLevel],
    ['qs_company_type', companyType],
    ['qs_token', token],
  ]

  for (const category of result.categoryScores) {
    qsParams.push([
      `${CATEGORY_FIELD_PREFIX}${category.categoryId}`,
      `${category.percentage}|${category.riskLevel}`,
    ])
  }

  for (const [questionId, answerValue] of Object.entries(answers)) {
    qsParams.push([`${ANSWER_FIELD_PREFIX}${questionId}`, answerValue])
  }

  if (successRedirectUrl) {
    qsParams.push(['qs_success_redirect', successRedirectUrl])
  }

  // Prefer the user-supplied full form URL (from MCAE "Embed Code") so we
  // support newer Lightning Form URL patterns like
  // `/l/{account}/{date}/{formId}` that the legacy host+account+id triplet
  // cannot express. Fall back to the legacy construction for older configs.
  const base = config.pardotFormUrl.length > 0
    ? config.pardotFormUrl
    : `https://${config.pardotFormHost}/l/${encodeURIComponent(config.pardotAccountId)}/${encodeURIComponent(config.pardotFormId)}/form.html`

  return appendQueryParams(base, qsParams)
}

function appendQueryParams(baseUrl: string, params: Array<[string, string]>): string {
  // Use the WHATWG URL parser so that any query/fragment that is already
  // present on the base URL (rare for a Pardot form, but supported by MCAE)
  // is preserved instead of being clobbered by a naive `?` concatenation.
  const url = new URL(baseUrl)
  for (const [key, value] of params) {
    url.searchParams.set(key, value)
  }
  return url.toString()
}

export function buildSuccessRedirectUrl(reportPageUrl: string, token: string): string {
  const url = new URL(reportPageUrl)
  url.searchParams.set('token', token)
  return url.toString()
}

export function summarizeCategoriesForLogging(scores: CategoryScore[]): string {
  return scores.map((c) => `${c.categoryId}:${c.percentage}%`).join(', ')
}
